/**
 * Offline POI queue (Phase 3 — LOC-2 · User POI tagging UX)
 *
 * A GPS failure OR a network failure must NEVER discard the user's tag.
 * This module owns the IndexedDB-backed queue that keeps unsent tags
 * around until the network returns, then drains them in FIFO order
 * against the UP-3a `POST /api/places/user` endpoint.
 *
 * Idempotency is guaranteed by the backend's `(owner_id, client_key)`
 * partial-unique index — retries always land on the same server row so
 * a page refresh mid-drain never creates a duplicate.
 */
const DB_NAME    = "find2near-poi-queue";
const DB_VERSION = 1;
const STORE      = "queued_pois";
const CH_NAME    = "poi-queue-changed";

let _dbP = null;

function openDb() {
  if (_dbP) return _dbP;
  _dbP = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "client_key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
  return _dbP;
}

/** Broadcast a change so a badge / pending-count listener can refresh. */
function notify() {
  try { new BroadcastChannel(CH_NAME).postMessage({ ts: Date.now() }); } catch { /* noop */ }
  try { window.dispatchEvent(new CustomEvent("f2n-poi-queue-changed")); } catch { /* noop */ }
}

/** Subscribe to queue-changed events. Returns an unsubscribe fn. */
export function onQueueChanged(cb) {
  const handler = () => cb();
  let bc = null;
  try {
    bc = new BroadcastChannel(CH_NAME);
    bc.onmessage = handler;
  } catch { /* older browser */ }
  window.addEventListener("f2n-poi-queue-changed", handler);
  window.addEventListener("online", handler);
  return () => {
    try { bc && bc.close(); } catch { /* noop */ }
    window.removeEventListener("f2n-poi-queue-changed", handler);
    window.removeEventListener("online", handler);
  };
}

function newClientKey() {
  // 128-bit random hex; more than enough entropy to avoid collisions
  // across devices and page reloads.
  const buf = new Uint8Array(16);
  (window.crypto || window.msCrypto).getRandomValues(buf);
  return "cli_" + Array.from(buf).map(b => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Enqueue a POI payload. Adds a `client_key` for idempotency +
 * `queued_at` timestamp. Rejects only when IndexedDB is entirely
 * unavailable — the caller must decide what to do in that case.
 */
export async function enqueuePoi(payload) {
  const db = await openDb();
  const client_key = payload.client_key || newClientKey();
  const row = {
    ...payload,
    client_key,
    queued_at: Date.now(),
    attempts: 0,
  };
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(row);
    tx.oncomplete = resolve;
    tx.onerror    = () => reject(tx.error);
  });
  notify();
  return row;
}

/** Return every queued row in FIFO order. */
export async function listQueue() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const out = [];
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).openCursor();
    req.onsuccess = () => {
      const cur = req.result;
      if (cur) { out.push(cur.value); cur.continue(); }
      else     { resolve(out.sort((a, b) => (a.queued_at || 0) - (b.queued_at || 0))); }
    };
    req.onerror = () => reject(req.error);
  });
}

export async function removeFromQueue(client_key) {
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(client_key);
    tx.oncomplete = resolve;
    tx.onerror    = () => reject(tx.error);
  });
  notify();
}

export async function queueSize() {
  try { return (await listQueue()).length; }
  catch { return 0; }
}

/**
 * Drain the queue against `POST /api/places/user`. Called manually
 * from a "Sync now" button and automatically whenever the browser
 * regains connectivity. `apiPost` is the app's axios wrapper — passed
 * in so this module stays framework-agnostic and easy to unit-test.
 *
 * Never rejects. Returns a summary `{ok, failed, remaining}` so the
 * caller can toast the outcome.
 */
export async function drainQueue(apiPost) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    return { ok: 0, failed: 0, remaining: await queueSize() };
  }
  let ok = 0, failed = 0;
  const rows = await listQueue();
  for (const row of rows) {
    try {
      const body = { ...row };
      delete body.queued_at; delete body.attempts;
      await apiPost("/places/user", body);
      await removeFromQueue(row.client_key);
      ok += 1;
    } catch (err) {
      // Bump attempts but keep the row. When the backend eventually
      // returns 4xx (validation error) for the same body we still keep
      // it queued so the user can inspect / delete it manually.
      failed += 1;
      try {
        const db = await openDb();
        const tx = db.transaction(STORE, "readwrite");
        const store = tx.objectStore(STORE);
        const g = store.get(row.client_key);
        g.onsuccess = () => {
          const cur = g.result;
          if (cur) {
            cur.attempts = (cur.attempts || 0) + 1;
            cur.last_error = String(err?.response?.status || err?.message || "unknown")
                             .slice(0, 120);
            store.put(cur);
          }
        };
      } catch { /* ignore bookkeeping failure */ }
    }
  }
  notify();
  return { ok, failed, remaining: await queueSize() };
}
